import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/";
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  login: (email, password) =>
    api.post("/api/v1/auth/login", { email, password }),
  getCurrentUser: () => api.get("/api/v1/auth/me"),
  changePassword: (data) => api.post("/api/v1/auth/change-password", data),
  forgotPassword: (email) =>
    api.post("/api/v1/auth/forgot-password", { email }),
  resetPassword: (token, new_password) =>
    api.post("/api/v1/auth/reset-password", { token, new_password }),
  sendVerification: () => api.post("/api/v1/auth/send-verification"),
  verifyEmail: (token) =>
    api.post("/api/v1/auth/verify-email", { token }),
  ssoConfig: () => api.get("/api/v1/auth/sso/config"),
};

// Organizations API
export const organizationsAPI = {
  list: () => api.get("/api/v1/organizations"),
  get: (id) => api.get(`/api/v1/organizations/${id}`),
  create: (data) => api.post("/api/v1/organizations", data),
  update: (id, data) => api.patch(`/api/v1/organizations/${id}`, data),
  delete: (id) => api.delete(`/api/v1/organizations/${id}`),
};

// Targets API
export const targetsAPI = {
  list: (organizationId) => {
    const params = organizationId ? { organization_id: organizationId } : {};
    return api.get("/api/v1/targets", { params });
  },
  get: (id) => api.get(`/api/v1/targets/${id}`),
  create: (data) => api.post("/api/v1/targets", data),
  update: (id, data) => api.patch(`/api/v1/targets/${id}`, data),
  delete: (id) => api.delete(`/api/v1/targets/${id}`),
};

// Scans API
export const scansAPI = {
  list: (targetId, status) => {
    const params = {};
    if (targetId) params.target_id = targetId;
    if (status) params.status = status;
    return api.get("/api/v1/scans", { params });
  },
  get: (id) => api.get(`/api/v1/scans/${id}`),
  create: (data) => api.post("/api/v2/scans/dispatch", data),
  cancel: (id) => api.post(`/api/v1/scans/${id}/cancel`),
  delete: (id) => api.delete(`/api/v1/scans/${id}`),
};

// Results API
export const resultsAPI = {
  list: (scanId, resultType, isNew, limit = 100) => {
    const params = { limit };
    if (scanId) params.scan_id = scanId;
    if (resultType) params.result_type = resultType;
    if (isNew !== undefined) params.is_new = isNew;
    return api.get("/api/v1/results", { params });
  },
};

// Modules API
export const modulesAPI = {
  list: () => api.get("/api/v1/modules"),
  listByType: (scanType) => api.get(`/api/v1/modules/by-type/${scanType}`),
};

export const settingsAPI = {
  get: () => api.get("/api/v1/settings"),
  updateAPIKeys: (data) => api.put("/api/v1/settings/api-keys", data),
  updateNotifications: (data) =>
    api.put("/api/v1/settings/notifications", data),
};

// CVE API
export const cveAPI = {
  check: (technology, version) => {
    const params = { technology };
    if (version) params.version = version;
    return api.get("/api/v1/cve/check", { params });
  },
};

// VulnHunt API
export const vulnHuntAPI = {
  search: (city) => api.post("/api/v1/vuln-hunt/search", { city }),
  getHostDetails: (ip) => api.get(`/api/v1/vuln-hunt/host/${ip}`),
};

// Beacons API
export const adminUsersAPI = {
  list: () => api.get('/api/v1/admin/users'),
  create: (data) => api.post('/api/v1/admin/users', data),
  update: (id, data) => api.patch(`/api/v1/admin/users/${id}`, data),
  delete: (id) => api.delete(`/api/v1/admin/users/${id}`),
};

export const beaconsAPI = {
  list: (skip = 0, limit = 50) => api.get(`/api/v2/beacons?skip=${skip}&limit=${limit}`),
  get: (id) => api.get(`/api/v2/beacons/${id}`),
  delete: (id) => api.delete(`/api/v2/beacons/${id}`),
  destroy: (id) => api.post(`/api/v2/beacons/${id}/destroy`),
  triggerScanner: (id, scanner) => api.post(`/api/v2/beacons/${id}/trigger?scanner=${scanner}`),
  update: (id, data) => api.patch(`/api/v2/beacons/${id}`, data),
  getTaskHistory: (id, skip = 0, limit = 50) => api.get(`/api/v2/beacons/${id}/tasks/history?skip=${skip}&limit=${limit}`),
  createDeployKey: (data) => api.post('/api/v2/beacons/deploy-keys', data),
  listDeployKeys: () => api.get('/api/v2/beacons/deploy-keys'),
  revokeDeployKey: (id) => api.delete(`/api/v2/beacons/deploy-keys/${id}`),
  listBeaconProfiles: () => api.get('/api/v2/beacons/profiles'),
  getInstallConfig: () => api.get('/api/v2/beacons/install-config'),
};

// Monitor (Network Monitor) - Asset Inventory + Vulnerability Findings.
// Backed by monitor_assets / monitor_services / monitor_vuln_findings, populated
// by the monitor binary (ARP discovery + service fingerprint + NVD/nuclei/ZAP).
export const monitorAPI = {
  dashboard: () => api.get('/api/v2/monitor/dashboard'),
  listVaults: () => api.get('/api/v2/monitor/vaults'),
  deleteVault: (vault) => api.delete('/api/v2/monitor/vaults', { params: { vault } }),
  scanVault: (vault, engine) => api.post('/api/v2/monitor/vaults/scan', { vault, engine }),
  listAssets: (params = {}) => api.get('/api/v2/monitor/assets', { params }),
  getAsset: (id) => api.get(`/api/v2/monitor/assets/${id}`),
  deleteAsset: (id) => api.delete(`/api/v2/monitor/assets/${id}`),
  bulkDeleteAssets: (ids) => api.post('/api/v2/monitor/assets/delete', { asset_ids: ids }),
  listFindings: (params = {}) => api.get('/api/v2/monitor/findings', { params }),
  resolveFinding: (id, reason) => api.post(`/api/v2/monitor/findings/${id}/resolve`, { reason }),
  // Wasabi Shinobi AI - risk triage (per vault) + asset identity/posture.
  getVaultTriage: (vault) => api.get('/api/v2/monitor/vaults/triage', { params: { vault } }),
  generateVaultTriage: (vault) => api.post('/api/v2/monitor/vaults/triage/generate', { vault }),
  identifyAsset: (id, force = false) =>
    api.post(`/api/v2/monitor/assets/${id}/identify`, null, { params: { force } }),
  identifyVault: (vault, force = false) =>
    api.post('/api/v2/monitor/vaults/identify', { vault, force }),
  // Reachability: every monitor beacon in the vault TCP-connects to the same
  // target set from its own address. Progress is read off the vault summary.
  probeReachability: (vault) =>
    api.post('/api/v2/monitor/vaults/reachability', { vault }),
  setAssetEolOverride: (id, data) =>
    api.post(`/api/v2/monitor/assets/${id}/eol-override`, data),
  getTopology: (vault) => api.get('/api/v2/monitor/topology', { params: { vault } }),
  narrateTopology: (vault) => api.post('/api/v2/monitor/topology/narrate', { vault }),
  getTopologyNarrative: (vault) => api.get('/api/v2/monitor/topology/narrative', { params: { vault } }),
};

// OSINT - the unified API surface for all OSINT sub-tabs.
//   Leaks tab (Part 1, live): IntelX-backed search + AI-triaged findings.
//   Network tab (Part 2, deferred): Shodan-backed company asset search.
//   Domains tab (Part 2, deferred): WHOIS via IntelX `whois` bucket.
export const osintAPI = {
  // -- Network tab (Shodan; existing /api/v2/osint/company endpoint) --
  companySearch: (query) => api.get('/api/v2/osint/company', { params: { q: query } }),
  // -- Leaks tab (IntelX) --
  createSearch: (data) => api.post('/api/v2/osint/leaks/searches', data),
  listSearches: (params = {}) => api.get('/api/v2/osint/leaks/searches', { params }),
  getSearch: (id) => api.get(`/api/v2/osint/leaks/searches/${id}`),
  refreshSearch: (id) => api.post(`/api/v2/osint/leaks/searches/${id}/refresh`),
  triageSearch: (id) => api.post(`/api/v2/osint/leaks/searches/${id}/triage`),
  listFindings: (params = {}) => api.get('/api/v2/osint/leaks/findings', { params }),
  getFinding: (id) => api.get(`/api/v2/osint/leaks/findings/${id}`),
  patchFinding: (id, data) => api.patch(`/api/v2/osint/leaks/findings/${id}`, data),
  dismissFinding: (id, data = {}) => api.post(`/api/v2/osint/leaks/findings/${id}/dismiss`, data),
  deepSearchFinding: (id) => api.post(`/api/v2/osint/leaks/findings/${id}/deep-search`),
  deleteSearch: (id) => api.delete(`/api/v2/osint/leaks/searches/${id}`),
  getSuggestions: () => api.get('/api/v2/osint/leaks/suggestions'),
  listNoiseSources: (params = {}) => api.get('/api/v2/osint/leaks/noise-sources', { params }),
  createNoiseSource: (data) => api.post('/api/v2/osint/leaks/noise-sources', data),
  patchNoiseSource: (id, data) => api.patch(`/api/v2/osint/leaks/noise-sources/${id}`, data),
  deleteNoiseSource: (id) => api.delete(`/api/v2/osint/leaks/noise-sources/${id}`),
  getBudget: () => api.get('/api/v2/osint/leaks/budget'),
  getAccountTrace: (account) =>
    api.get('/api/v2/osint/leaks/account-trace', { params: { account } }),
  listAccounts: (params = {}) => api.get('/api/v2/osint/leaks/accounts', { params }),
  getCloudQuota: () => api.get('/api/v2/scans/cloud-quota'),
  // -- Leak-Site Sweep (beacon-egress leak-site harvester) --
  createSweep: (data) => api.post('/api/v2/osint/leak-sweep/runs', data),
  listSweeps: (params = {}) => api.get('/api/v2/osint/leak-sweep/runs', { params }),
  getSweep: (id) => api.get(`/api/v2/osint/leak-sweep/runs/${id}`),
  deleteSweep: (id) => api.delete(`/api/v2/osint/leak-sweep/runs/${id}`),
  exportSweep: (id) => api.get(`/api/v2/osint/leak-sweep/runs/${id}/export`),
  retriageSweep: (id) => api.post(`/api/v2/osint/leak-sweep/runs/${id}/retriage`),
};

// Quick Scan API
export const quickScanAPI = {
  getModules: (targetType) => api.get('/api/v2/scans/modules', { params: targetType ? { target_type: targetType } : {} }),
  launch: (data) => api.post('/api/v2/scans/quick', data),
  recent: (params = {}) => api.get('/api/v2/scans/recent', { params }),
  getExecutionTargets: (scanner) => api.get('/api/v2/scans/execution-targets', { params: scanner ? { scanner } : {} }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/api/v2/dashboard/stats'),
  getCompliance: () => api.get('/api/v2/dashboard/compliance'),
};

// Assets API
export const assetsAPI = {
  list: (params = {}) => api.get('/api/v2/assets', { params }),
  get: (ip) => api.get(`/api/v2/assets/${encodeURIComponent(ip)}`),
};

// Vulnerabilities API
export const vulnerabilitiesAPI = {
  list: (params = {}) => api.get('/api/v2/vulnerabilities', { params }),
};

// Phase 2 (2026-05-14): portfolio-wide Dashboard payload (sidebar entry).
export const portfolioDashboardAPI = {
  get: () => api.get('/api/v2/dashboard/portfolio').then(r => r.data),
};

// Phase 2 (2026-05-14): admin operations for the per-finding category classifier.
export const adminFindingsAPI = {
  classifyStatus: () => api.get('/api/v1/admin/findings/classify-status').then(r => r.data),
  classifyAll: () => api.post('/api/v1/admin/findings/classify-all').then(r => r.data),
  // 2026-05-14 fix-pass: retroactively mark per-path auto-extracted findings on
  // catchall hosts (>=5 distinct sensitive-token paths returning 200) as false-
  // positive with a clear reason. New runs apply the same rule at extraction time.
  suppressCatchall: () => api.post('/api/v1/admin/findings/suppress-catchall').then(r => r.data),
};

// Threat Intelligence API
export const threatIntelAPI = {
  getTopology: (beaconId) => api.get(`/api/v2/threat-intel/topology${beaconId ? `?beacon_id=${beaconId}` : ''}`),
  getTopologyStats: (beaconId) => api.get(`/api/v2/threat-intel/topology/stats${beaconId ? `?beacon_id=${beaconId}` : ''}`),
  purgeStaleTopology: () => api.delete('/api/v2/threat-intel/topology/stale'),
  listAlerts: (params = {}) => api.get('/api/v2/threat-intel/alerts', { params }),
  updateAlertStatus: (id, status) => api.post(`/api/v2/threat-intel/alerts/${id}/status`, { status }),
  listEntries: (params = {}) => api.get('/api/v2/threat-intel/entries', { params }),
  addWhitelist: (data) => api.post('/api/v2/threat-intel/entries/whitelist', data),
  addBlacklist: (data) => api.post('/api/v2/threat-intel/entries/blacklist', data),
  deleteEntry: (id) => api.delete(`/api/v2/threat-intel/entries/${id}`),
  // Threat Feeds
  getFeeds: (skip = 0, limit = 50) => api.get('/api/v2/threat-intel/feeds', { params: { skip, limit } }),
  createFeed: (data) => api.post('/api/v2/threat-intel/feeds', data),
  updateFeed: (id, data) => api.patch(`/api/v2/threat-intel/feeds/${id}`, data),
  deleteFeed: (id) => api.delete(`/api/v2/threat-intel/feeds/${id}`),
  syncFeed: (id) => api.post(`/api/v2/threat-intel/feeds/${id}/sync`),
  // AI Analysis
  aiSummarize: (beaconId) => api.post('/api/v2/ai/summarize', { beacon_id: beaconId || null }),
  aiTriage: (alertId) => api.post('/api/v2/ai/triage', { alert_id: alertId }),
  aiTriageBatch: (alertIds) => api.post('/api/v2/ai/triage/batch', { alert_ids: alertIds }),
  aiGetLatest: () => api.get('/api/v2/ai/history/latest'),
  aiGetHistory: (skip = 0, limit = 20) => api.get('/api/v2/ai/history', { params: { skip, limit } }),
  aiGetAnalysis: (id) => api.get(`/api/v2/ai/history/${id}`),
  aiDeleteAnalysis: (id) => api.delete(`/api/v2/ai/history/${id}`),
  aiDeleteAllAnalyses: () => api.delete('/api/v2/ai/history'),
};

// Findings API (CVE-based findings from scan enrichment)
export const findingsAPI = {
  list: (params = {}) => api.get('/api/v2/findings', { params }),
  unseenCount: () => api.get('/api/v2/findings/unseen-count'),
  markSeen: () => api.post('/api/v2/findings/mark-seen'),
};

// Vulnerability Scan API
export const vulnScanAPI = {
  getProfiles: () => api.get('/api/v2/vulnerability-scans/profiles'),
  getProfileOptions: () => api.get('/api/v2/vulnerability-scans/profile-options'),
  create: (data) => api.post('/api/v2/vulnerability-scans', data),
  list: (params) => api.get('/api/v2/vulnerability-scans', { params }),
  get: (id) => api.get(`/api/v2/vulnerability-scans/${id}`),
  run: (id, body) => api.post(`/api/v2/vulnerability-scans/${id}/run`, body || {}),
  stop: (id) => api.post(`/api/v2/vulnerability-scans/${id}/stop`),
  delete: (id) => api.delete(`/api/v2/vulnerability-scans/${id}`),
  getRuns: (id, params) => api.get(`/api/v2/vulnerability-scans/${id}/runs`, { params }),
  findByScanId: (scanId) => api.get(`/api/v2/vulnerability-scans/by-scan-id/${scanId}`),
  getFindings: (id, params) => api.get(`/api/v2/vulnerability-scans/${id}/findings`, { params }),
  updateFindingStatus: (findingId, status) => api.patch(`/api/v2/vulnerability-scans/findings/${findingId}`, { status }),
  backfillFindings: () => api.post('/api/v2/vulnerability-scans/backfill-findings'),
};

// CVE enrichment (Phase B): batch lookup against NVD with PG cache
export const cveEnrichAPI = {
  enrich: (items) => api.post('/api/v2/cve/enrich', { items }).then(r => r.data),
};

// Subdomain Monitoring (recurring subdomain_enum with new-finding badge)
export const subdomainMonitorsAPI = {
  list: () => api.get('/api/v2/subdomain-monitors').then(r => r.data),
  get: (id) => api.get(`/api/v2/subdomain-monitors/${id}`).then(r => r.data),
  create: (body) => api.post('/api/v2/subdomain-monitors', body).then(r => r.data),
  update: (id, body) => api.patch(`/api/v2/subdomain-monitors/${id}`, body).then(r => r.data),
  remove: (id) => api.delete(`/api/v2/subdomain-monitors/${id}`),
  view: (id) => api.post(`/api/v2/subdomain-monitors/${id}/view`),
  runNow: (id) => api.post(`/api/v2/subdomain-monitors/${id}/run-now`).then(r => r.data),
};

// Reports API
export const reportsAPI = {
  create: (data) => api.post('/api/v2/reports', data),
  list: (params) => api.get('/api/v2/reports', { params }),
  get: (id) => api.get(`/api/v2/reports/${id}`),
  download: (id) => api.get(`/api/v2/reports/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/api/v2/reports/${id}`),
  preview: (data) => api.post('/api/v2/reports/preview', data),
};

// Billing API
// Sites API
export const sitesAPI = {
  list: () => api.get('/api/v2/sites'),
  rename: (id, displayName) => api.patch(`/api/v2/sites/${id}`, { display_name: displayName }),
};

// Agentic pentest (v2). One hypothesis, one agent, one verdict. Red Teamer only:
// these endpoints start autonomous work against live hosts.
export const agenticAPI = {
  overview: (pentestId) =>
    api.get(`/api/v2/agentic/${pentestId}/overview`).then(r => r.data),
  listHypotheses: (pentestId, params = {}) =>
    api.get(`/api/v2/agentic/${pentestId}/hypotheses`, { params }).then(r => r.data),
  createHypothesis: (pentestId, payload) =>
    api.post(`/api/v2/agentic/${pentestId}/hypotheses`, payload).then(r => r.data),
  // The Bench: a hypothesis with its runs, turns and evidence in one call.
  hypothesisDetail: (pentestId, hypothesisId) =>
    api.get(`/api/v2/agentic/${pentestId}/hypotheses/${hypothesisId}`).then(r => r.data),
  abandonHypothesis: (pentestId, hypothesisId) =>
    api.post(`/api/v2/agentic/${pentestId}/hypotheses/${hypothesisId}/abandon`).then(r => r.data),
  retaskHypothesis: (pentestId, hypothesisId, payload = {}) =>
    api.post(`/api/v2/agentic/${pentestId}/hypotheses/${hypothesisId}/retask`, payload).then(r => r.data),
  seed: (pentestId) =>
    api.post(`/api/v2/agentic/${pentestId}/seed`).then(r => r.data),
  advance: (pentestId) =>
    api.post(`/api/v2/agentic/${pentestId}/advance`).then(r => r.data),
  // Stop driving the engagement without throwing any work away. Reset deletes
  // the agentic work; this only tells the scheduler to stop offering to take
  // the next slice, and advance starts it again.
  pause: (pentestId) =>
    api.post(`/api/v2/agentic/${pentestId}/pause`).then(r => r.data),
  // Extends a parked run so it resumes with its ledger intact, rather than
  // starting a fresh one that would re-do captured work.
  topUp: (pentestId, runId, payload) =>
    api.post(`/api/v2/agentic/${pentestId}/runs/${runId}/top-up`, payload).then(r => r.data),
  // Assembled from the ledger. summarise=true adds the one model-written
  // paragraph; every other number is a row either way.
  report: (pentestId, summarise = false) =>
    api.get(`/api/v2/agentic/${pentestId}/report`, { params: { summarise } }).then(r => r.data),

  // The plan: the operator gate. Nothing touches a host before it is approved,
  // so these five calls are the whole start-of-engagement decision.
  plan: (pentestId) =>
    api.get(`/api/v2/agentic/${pentestId}/plan`).then(r => r.data),
  planVersions: (pentestId) =>
    api.get(`/api/v2/agentic/${pentestId}/plans`).then(r => r.data),
  // Queued, not run inline: drafting is a framing pass plus a model turn over
  // the whole engagement, which is minutes.
  draftPlan: (pentestId) =>
    api.post(`/api/v2/agentic/${pentestId}/plan/draft`).then(r => r.data),
  // `exclude` carries the node ids the operator trimmed. Their hypotheses stay
  // `planned` rather than being deleted, so the report can say they were
  // proposed and declined.
  approvePlan: (pentestId, version, payload = {}) =>
    api.post(`/api/v2/agentic/${pentestId}/plans/${version}/approve`, payload).then(r => r.data),
  rejectPlan: (pentestId, version, note) =>
    api.post(`/api/v2/agentic/${pentestId}/plans/${version}/reject`, { note }).then(r => r.data),
  // Operator-typed names go through the same screening as generated ones, so a
  // 400 here is the wordlist refusing something, not a bug. Pass
  // {reroll: true} to draw a fresh one instead.
  // What a reset would remove, without removing it. A GET that previews the
  // DELETE and shares its counting code, so the number the operator agrees to
  // is the number that goes.
  resetPreview: (pentestId, deep = false) =>
    api.get(`/api/v2/agentic/${pentestId}/reset`, { params: { deep } }).then(r => r.data),
  // `confirm` must equal the engagement's name exactly. A typed name is the one
  // confirmation that cannot be given by accident on the wrong engagement.
  reset: (pentestId, confirm, deep = false) =>
    api.post(`/api/v2/agentic/${pentestId}/reset`, { confirm }, { params: { deep } })
      .then(r => r.data),

  // A Lead can fail on a model response that ran out of room and did not
  // parse. `failed` is terminal and nothing resumes it, so this gives it a
  // fresh run without resetting the engagement. Its context is assembled from
  // durable rows every turn, so the new run picks up exactly where the old one
  // was looking.
  restartLead: (pentestId) =>
    api.post(`/api/v2/agentic/${pentestId}/lead/restart`).then(r => r.data),
  // Tell the Lead its report has been read. `report_to_operator` used to park
  // on awaiting_authority, which only a plan decision or a level grant could
  // clear, so a Lead that reported was finished for good: pentest 36's sat that
  // way for two days. This is the answer to that park and nothing else answers it.
  acknowledgeReport: (pentestId) =>
    api.post(`/api/v2/agentic/${pentestId}/lead/acknowledge`).then(r => r.data),
  setCodename: (pentestId, runId, payload) =>
    api.patch(`/api/v2/agentic/${pentestId}/runs/${runId}/codename`, payload).then(r => r.data),
};

// Penetration Tests API (v2 - root surface for the new PT workflow)
export const pentestsAPI = {
  list: (params = {}) => api.get('/api/v2/pentests', { params }).then(r => r.data),
  get: (id) => api.get(`/api/v2/pentests/${id}`).then(r => r.data),
  create: (payload) => api.post('/api/v2/pentests', payload).then(r => r.data),
  update: (id, payload) => api.patch(`/api/v2/pentests/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/api/v2/pentests/${id}`),
  getQuestionnaire: (id) => api.get(`/api/v2/pentests/${id}/questionnaire`).then(r => r.data),
  updateQuestionnaire: (id, sections) =>
    api.patch(`/api/v2/pentests/${id}/questionnaire`, { sections }).then(r => r.data),

  // Scope targets carry an exploitation level; the ROE returned here is the
  // parsed, defaulted one the backend will actually enforce, not the raw blob.
  // Red Teamer only: these routes decide how far an agent may go and they hold
  // credentials, so a Wasabian gets a 403 rather than an empty list.
  scope: {
    get: (pentestId) =>
      api.get(`/api/v2/pentests/${pentestId}/scope`).then(r => r.data),
    addTarget: (pentestId, payload) =>
      api.post(`/api/v2/pentests/${pentestId}/scope-targets`, payload).then(r => r.data),
    setLevel: (pentestId, targetId, payload) =>
      api.patch(
        `/api/v2/pentests/${pentestId}/scope-targets/${targetId}/level`, payload,
      ).then(r => r.data),
    // Its own call rather than a field on setLevel, deliberately: bundling them
    // would let one request reclassify the environment that governs the friction
    // on raising a level, in the same request that raises it.
    //
    // The backend has always returned `suggested_sensitivity` on every target
    // and nothing read it. Its own docstring calls it "a SUGGESTION for the UI
    // to pre-select", so the popover now does exactly that.
    setSensitivity: (pentestId, targetId, sensitivity) =>
      api.patch(
        `/api/v2/pentests/${pentestId}/scope-targets/${targetId}/sensitivity`,
        { sensitivity },
      ).then(r => r.data),
    removeTarget: (pentestId, targetId) =>
      api.delete(`/api/v2/pentests/${pentestId}/scope-targets/${targetId}`),
  },

  // Differential probe. The level a probe requires is derived server-side from
  // its payloads, so a 409 here means the engagement's own rules refuse it and
  // the detail carries the level that would unblock it.
  probes: {
    launch: (pentestId, payload) =>
      api.post(`/api/v2/pentests/${pentestId}/probes`, payload).then(r => r.data),
  },

  // The secret is write-only. Responses carry a masked preview and a `usable`
  // flag; there is no read-back endpoint by design.
  identities: {
    list: (pentestId) =>
      api.get(`/api/v2/pentests/${pentestId}/identities`).then(r => r.data),
    create: (pentestId, payload) =>
      api.post(`/api/v2/pentests/${pentestId}/identities`, payload).then(r => r.data),
    remove: (pentestId, identityId) =>
      api.delete(`/api/v2/pentests/${pentestId}/identities/${identityId}`),
    // Rotation, not re-creation. The id survives, and the id is what the
    // agents' context, an in-flight ProbeAction and a finding's auto_origin
    // all reference; delete-and-re-add renumbers it under them.
    replaceSecret: (pentestId, identityId, payload) =>
      api.put(`/api/v2/pentests/${pentestId}/identities/${identityId}/secret`, payload)
        .then(r => r.data),
  },
  // What the operator KNOWS, as opposed to what a scanner found. Retracting
  // returns the row rather than 204: withdrawing is soft, because a published
  // finding's provenance may still name the fact.
  facts: {
    list: (pentestId, params = {}) =>
      api.get(`/api/v2/pentests/${pentestId}/facts`, { params }).then(r => r.data),
    create: (pentestId, payload) =>
      api.post(`/api/v2/pentests/${pentestId}/facts`, payload).then(r => r.data),
    retract: (pentestId, factId) =>
      api.delete(`/api/v2/pentests/${pentestId}/facts/${factId}`).then(r => r.data),
  },
  runs: {
    list: (pentestId) =>
      api.get(`/api/v2/pentests/${pentestId}/runs`).then(r => r.data),
    get: (pentestId, kind, runId, params = {}) =>
      api.get(`/api/v2/pentests/${pentestId}/runs/${kind}/${runId}`, { params }).then(r => r.data),
    remove: (pentestId, kind, runId) =>
      api.delete(`/api/v2/pentests/${pentestId}/runs/${kind}/${runId}`),
  },
  findings: {
    list: (pentestId) =>
      api.get(`/api/v2/pentests/${pentestId}/findings`).then(r => r.data),
    get: (pentestId, findingId) =>
      api.get(`/api/v2/pentests/${pentestId}/findings/${findingId}`).then(r => r.data),
    create: (pentestId, payload) =>
      api.post(`/api/v2/pentests/${pentestId}/findings`, payload).then(r => r.data),
    update: (pentestId, findingId, payload) =>
      api.patch(`/api/v2/pentests/${pentestId}/findings/${findingId}`, payload).then(r => r.data),
    remove: (pentestId, findingId) =>
      api.delete(`/api/v2/pentests/${pentestId}/findings/${findingId}`).then(r => r.data),
    // Accept a provisional finding into the score. An agentic finding arrives
    // having survived the refuter, which is evidence rather than the operator's
    // judgement, so it is visible and uncounted until this is clicked.
    accept: (pentestId, findingId) =>
      api.post(`/api/v2/pentests/${pentestId}/findings/${findingId}/accept`).then(r => r.data),
    markFalsePositive: (pentestId, findingId, body) =>
      api.post(`/api/v2/pentests/${pentestId}/findings/${findingId}/false-positive`, body).then(r => r.data),
    unmarkFalsePositive: (pentestId, findingId) =>
      api.delete(`/api/v2/pentests/${pentestId}/findings/${findingId}/false-positive`).then(r => r.data),
  },
  auditLog: {
    list: (pentestId, { limit = 200, offset = 0 } = {}) =>
      api.get(`/api/v2/pentests/${pentestId}/audit-log`, { params: { limit, offset } }).then(r => r.data),
  },
  // Phase 2 (2026-05-14): per-PT Dashboards tab payload.
  dashboard: (pentestId) =>
    api.get(`/api/v2/pentests/${pentestId}/dashboard`).then(r => r.data),
  aiInsights: {
    get: (pentestId) =>
      api.get(`/api/v2/pentests/${pentestId}/ai-insights`).then(r => r.data),
    generateSummary: (pentestId) =>
      api.post(`/api/v2/pentests/${pentestId}/ai-insights/summary`).then(r => r.data),
    analyzeFinding: (pentestId, findingId) =>
      api.post(`/api/v2/pentests/${pentestId}/findings/${findingId}/ai-analysis`).then(r => r.data),
    // 2026-05-14 Phase 1: lazy-generates 5-8 remediation workstreams clustering
    // open findings. Server caches up to 24h or until findings change; pass
    // force=true to bypass.
    generateRemediationThemes: (pentestId, { force = false } = {}) =>
      api.post(`/api/v2/pentests/${pentestId}/ai-insights/remediation-themes`, null, {
        params: force ? { force: true } : undefined,
      }).then(r => r.data),
    // 2026-05-14 fix-pass: per-finding AI test command (LLM-generated safe
    // verification command using the kali_command allowlist) + run-on-beacon
    // dispatch. The persisted command is also re-validated server-side at
    // dispatch time so a stale allowlist entry can't slip through.
    getTestCommand: (pentestId, findingId) =>
      api.get(`/api/v2/pentests/${pentestId}/findings/${findingId}/ai-test-command`).then(r => r.data),
    generateTestCommand: (pentestId, findingId) =>
      api.post(`/api/v2/pentests/${pentestId}/findings/${findingId}/ai-test-command`).then(r => r.data),
    runTest: (pentestId, findingId, { beaconId = null } = {}) =>
      api.post(`/api/v2/pentests/${pentestId}/findings/${findingId}/run-test`, {
        beacon_id: beaconId,
      }).then(r => r.data),
    // Returns the latest verification verdict for this finding
    // ({verdict: confirmed|refuted|inconclusive, summary, evidence_snippet})
    // or null if no Run-on-beacon test has been auto-verified yet.
    getTestVerification: (pentestId, findingId) =>
      api.get(`/api/v2/pentests/${pentestId}/findings/${findingId}/ai-test-verification`).then(r => r.data),
  },
  reports: {
    list: (pentestId) =>
      api.get(`/api/v2/pentests/${pentestId}/reports`).then(r => r.data),
    generate: (pentestId, kind) =>
      api.post(`/api/v2/pentests/${pentestId}/reports`, { kind }).then(r => r.data),
    download: (pentestId, reportId) =>
      api.get(`/api/v2/pentests/${pentestId}/reports/${reportId}/download`, {
        responseType: 'blob',
      }),
    remove: (pentestId, reportId) =>
      api.delete(`/api/v2/pentests/${pentestId}/reports/${reportId}`),
  },
  retests: {
    list: (pentestId) =>
      api.get(`/api/v2/pentests/${pentestId}/retests`).then(r => r.data),
    create: (pentestId) =>
      api.post(`/api/v2/pentests/${pentestId}/retests`).then(r => r.data),
  },
};

// AI Orchestrator API (v2 - Delta design)
export const orchestratorAPI = {
  listForPentest: (pentestId) =>
    api.get(`/api/v2/orchestrator/pentests/${pentestId}/engagements`).then(r => r.data),
  create: (pentestId, payload) =>
    api.post(`/api/v2/orchestrator/pentests/${pentestId}/orchestrate`, payload).then(r => r.data),
  get: (engagementId) =>
    api.get(`/api/v2/orchestrator/engagements/${engagementId}`).then(r => r.data),
  regenerate: (engagementId) =>
    api.post(`/api/v2/orchestrator/engagements/${engagementId}/regenerate`).then(r => r.data),
  approve: (engagementId, payload = {}) =>
    api.post(`/api/v2/orchestrator/engagements/${engagementId}/approve`, payload).then(r => r.data),
  stop: (engagementId) =>
    api.post(`/api/v2/orchestrator/engagements/${engagementId}/stop`).then(r => r.data),
  decideEscalation: (engagementId, payload) =>
    api.post(`/api/v2/orchestrator/engagements/${engagementId}/escalation/decide`, payload).then(r => r.data),
  audit: (engagementId) =>
    api.get(`/api/v2/orchestrator/engagements/${engagementId}/audit`).then(r => r.data),
  report: (engagementId) =>
    api.get(`/api/v2/orchestrator/engagements/${engagementId}/report`).then(r => r.data),
  taskOutput: (taskId, { afterSeq = 0, limit = 2000 } = {}) =>
    api.get(`/api/v2/beacons/tasks/${taskId}/output`, {
      params: { after_seq: afterSeq, limit },
    }).then(r => r.data),
};

// Wasabi Red Team Status Page (open to both roles)
export const statusPageAPI = {
  get: ({ includeInProgress = true } = {}) =>
    api.get('/api/v2/status-page', {
      params: { include_in_progress: includeInProgress },
    }).then(r => r.data),
};

// Agent Pentesting: adversarial QA/red-team suite for a customer's LLM
// agent. Its own product, separate from the Pentests (PT) plane above -
// these calls return the raw axios response (callers read res.data), which
// is the one deliberate exception to the .then(r => r.data) convention
// used elsewhere in this file.
export const agentPentestAPI = {
  list: () => api.get('/api/v2/agent-pentests'),
  get: (id) => api.get(`/api/v2/agent-pentests/${id}`),
  create: (body) => api.post('/api/v2/agent-pentests', body),
  update: (id, body) => api.put(`/api/v2/agent-pentests/${id}`, body),
  remove: (id) => api.delete(`/api/v2/agent-pentests/${id}`),
  startRun: (id) => api.post(`/api/v2/agent-pentests/${id}/runs`),
  listRuns: (id) => api.get(`/api/v2/agent-pentests/${id}/runs`),
  getRun: (runId) => api.get(`/api/v2/agent-pentests/runs/${runId}`),
  getResult: (resultId) => api.get(`/api/v2/agent-pentests/results/${resultId}`),
};

export default api;
